import React, { useEffect, useRef, useState } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ProgressBar } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import CloseIcon from 'src/shared/icons/times-solid.svg';
import styled, { keyframes } from 'styled-components';
import { useDialogManager } from '../../../widgets/dialog';
import { Icon } from '../../../widgets/icon';
import { showErrorNotification } from '../../../widgets/notificationToaster';

enum TaskStatus {
    uploading,
    progress,
    checking,
    success,
    completed_success,
    completed_error
}

type Task = {
    status: TaskStatus;
    uploadProgress?: number;
    meshes: BABYLON.AbstractMesh[];
};

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(6px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
`;

const UploadMessage = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
`;

const dotsBounce = keyframes`
    0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
`;

const DotsLoader = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;

    span {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #ffffff;
        animation: ${dotsBounce} 1.4s ease-in-out infinite both;
    }

    span:nth-child(1) {
        animation-delay: -0.32s;
    }
    span:nth-child(2) {
        animation-delay: -0.16s;
    }
    span:nth-child(3) {
        animation-delay: 0s;
    }
`;

const ProgressContainer = styled.div`
    width: 400px;
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    ${Icon} {
        width: 14px;
        height: 14px;
        min-width: 0;
        fill: currentColor;
    }
`;

export const UploadDialog = ({
    files,
    damProductAssetUrl,
    onComplete
}: {
    files?: FileList;
    damProductAssetUrl?: string;
    onComplete: (meshes: BABYLON.AbstractMesh[]) => void;
}) => {
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);
    const [progress, setProgress] = useState(0);
    const { currentDialogId, closeDialog } = useDialogManager();

    const currentTaskRef = useRef<Task | null | undefined>(null);

    useEffect(() => {
        sceneEditor?.loadSceneSourceFiles(
            files ? Array.from(files) : undefined,
            damProductAssetUrl ? damProductAssetUrl : undefined,
            () => null,
            () => {
                showErrorNotification({
                    message: T._('Failed to upload the files.', 'Admin')
                });

                closeDialog(currentDialogId);
            }
        );
    }, []);

    useEffect(() => {
        sceneEditor?.addSceneSourceTaskCheckListener(handleSceneTaskCheck);

        return () => sceneEditor?.removeSceneSourceTaskCheckListener(handleSceneTaskCheck);
    }, []);

    const handleSceneTaskCheck = async () => {
        const task = sceneEditor?.sceneSourceTasks[0];

        if (task) currentTaskRef.current = task as unknown as Task; // I can't import them from the lib because it requires the JS to be loaded first

        if (currentTaskRef.current) {
            if (currentTaskRef.current.status === TaskStatus.uploading) setProgress(currentTaskRef.current.uploadProgress!);

            if (currentTaskRef.current.status === TaskStatus.progress) {
                setProgress(100);
            }

            if (currentTaskRef.current.status == TaskStatus.completed_error) {
                showErrorNotification({
                    message: T._('Failed to upload the files.', 'Admin')
                });

                closeDialog(currentDialogId);
                currentTaskRef.current = undefined;
                return;
            }

            if (currentTaskRef.current && !task) {
                onComplete(currentTaskRef.current.meshes);

                currentTaskRef.current = undefined;
            }
        }
    };

    const handleCancel = () => {
        const sceneEditorWithCancel = sceneEditor as (typeof sceneEditor & { cancelSceneSourceTasks?: () => void }) | null;
        sceneEditorWithCancel?.cancelSceneSourceTasks?.();
        currentTaskRef.current = undefined;
        closeDialog(currentDialogId);
    };

    const [messagePhase, setMessagePhase] = useState(0);
    useEffect(() => {
        const t1 = setTimeout(() => setMessagePhase(1), 5000);
        const t2 = setTimeout(() => setMessagePhase(2), 10000);
        const t3 = setTimeout(() => setMessagePhase(3), 15000);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, []);

    const uploadMessage =
        messagePhase === 3
            ? T._('Your model might be a bit heavy, this could take a while\u2026', 'Admin')
            : messagePhase === 2
              ? T._('Almost there, just a few more seconds\u2026', 'Admin')
              : messagePhase === 1
                ? T._("...hold on, we're getting your model ready\u2026", 'Admin')
                : T._('Uploading your 3D model...', 'Admin');

    return (
        <Overlay>
            <DotsLoader>
                <span />
                <span />
                <span />
            </DotsLoader>
            <UploadMessage>{uploadMessage}</UploadMessage>
            <ProgressContainer>
                <ProgressBar progress={progress} />
            </ProgressContainer>
            <CloseButton onClick={handleCancel} aria-label={T._('Cancel', 'Admin')}>
                <Icon>
                    <CloseIcon />
                </Icon>
            </CloseButton>
        </Overlay>
    );
};
